import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { CodesTab } from './_components/CodesTab';
import { CompanyHeader } from './_components/CompanyHeader';
import { MembersTab } from './_components/MembersTab';
import { OverviewTab } from './_components/OverviewTab';
import { ReportsTab } from './_components/ReportsTab';
import { StatusTab } from './_components/StatusTab';
import { useAdminOrg } from '../../api/admin-orgs';
import { httpStatusOf } from '../../api/errors';
import { RequirePermission } from '../../auth';

/**
 * `/companies/:id` — the company card (AUT-403), after
 * `companies-detail.html`: header + tabs Обзор / Сотрудники / Коды /
 * Отчёты / Статус. The tab lives in `?tab=` (a shareable link, survives a
 * reload); junk falls back to «Обзор». One `GET /admin/orgs/:id` feeds the
 * header and every tab; the members / member-code reads are per tab.
 */
const TABS = ['overview', 'members', 'codes', 'reports', 'status'] as const;
type TabId = (typeof TABS)[number];

const isTab = (v: string | null): v is TabId => TABS.includes(v as TabId);

const CompanyDetail = ({ id }: { id: string }): ReactElement => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const query = useAdminOrg(id);
  const raw = params.get('tab');
  const tab: TabId = isTab(raw) ? raw : 'overview';
  const go = (next: TabId): void =>
    setParams(next === 'overview' ? {} : { tab: next }, { replace: true });

  if (query.isLoading) return <CircularProgress />;
  if (!query.data) {
    const key =
      httpStatusOf(query.error) === 404
        ? 'companies.detail.notFound'
        : 'companies.detail.loadError';
    return <Alert severity="error">{t(key)}</Alert>;
  }
  const org = query.data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CompanyHeader org={org} />
      <Tabs
        value={tab}
        onChange={(_, next: TabId) => go(next)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.map((id) => (
          <Tab
            key={id}
            value={id}
            label={
              id === 'members'
                ? t('companies.detail.tabs.membersCount', { count: org.activeMembers })
                : t(`companies.detail.tabs.${id}`)
            }
          />
        ))}
      </Tabs>
      {tab === 'overview' ? <OverviewTab org={org} /> : null}
      {tab === 'members' ? <MembersTab org={org} onChangeOwner={() => go('codes')} /> : null}
      {tab === 'codes' ? <CodesTab org={org} /> : null}
      {tab === 'reports' ? <ReportsTab /> : null}
      {tab === 'status' ? <StatusTab org={org} /> : null}
    </Box>
  );
};

const CompanyDetailRoute = (): ReactElement => {
  const { id } = useParams<{ id: string }>();
  return <CompanyDetail key={id} id={id ?? ''} />;
};

export const CompanyDetailPage = (): ReactElement => (
  <RequirePermission perm="verify_inspectors">
    <CompanyDetailRoute />
  </RequirePermission>
);
